import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Calendar, Eye, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function News() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await fetch(`${API}/news?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setStories(data.stories || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <Link to="/" className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8 sm:h-10" />
            
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/check" className="hidden md:block">
              <Button variant="outline" className="border-yellow-500 text-yellow-400" data-testid="check-tool-btn">
                Check a Tool
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" data-testid="login-btn">Login</Button>
            </Link>
            <Link to="/register">
              <Button className="btn-primary" data-testid="register-btn">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-5xl mx-auto">
        {/* Back Link */}
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl lg:text-5xl font-bold uppercase mb-2">News & Updates</h1>
          <p className="text-muted-foreground">Stay up to date with the latest from TOOLIO</p>
        </div>

        {/* Stories List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading stories...</p>
          </div>
        ) : stories.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <h3 className="font-heading text-xl uppercase mb-2">No Stories Yet</h3>
              <p className="text-muted-foreground">Check back soon for news and updates.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {stories.map((story) => (
              <Link key={story.story_id} to={`/news/${story.slug}`} data-testid={`story-card-${story.story_id}`}>
                <Card className="border-border bg-card hover:border-primary/50 transition-colors overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    {/* Featured Image */}
                    {story.featured_image && (
                      <div className="md:w-64 md:flex-shrink-0 h-48 md:h-auto bg-black/20">
                        <img 
                          src={story.featured_image.startsWith('/api') ? API_BASE + story.featured_image : story.featured_image}
                          alt={story.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    
                    <CardContent className="p-6 flex-1">
                      {/* Tags */}
                      {story.tags && story.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {story.tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs border-primary/50 text-primary">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Title */}
                      <h2 className="font-heading text-xl lg:text-2xl font-bold uppercase mb-2 group-hover:text-primary transition-colors">
                        {story.title}
                      </h2>
                      
                      {/* Excerpt */}
                      {story.excerpt && (
                        <p className="text-muted-foreground mb-4 line-clamp-2">
                          {story.excerpt}
                        </p>
                      )}
                      
                      {/* Meta */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(story.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-4 h-4" />
                          {story.views || 0} views
                        </span>
                        <span>By {story.author_name}</span>
                      </div>
                      
                      {/* Read More */}
                      <div className="mt-4 text-primary flex items-center text-sm font-medium">
                        Read more <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 lg:px-12 border-t border-border mt-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 max-w-5xl mx-auto">
          <div className="flex items-center gap-2">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
            
          </div>
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} TOOLIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
